import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, X } from 'lucide-react';
import { parseSPDate, formatTelefoneBR } from '@fiodecobre/shared';
import { useAuth } from '../lib/auth';
import { Avatar } from './Avatar';
import { atendimentoApi } from '../modules/atendimento/lib/api';
import { useAlertaFaixa } from './alertas/AlertasContexto';

interface Perdido {
  id: number;
  nome: string | null;
  telefone: string | null;
  jid: string;
  foto_url: string | null;
  atendente_nome: string | null;
  status_em: string;
}

const POLLING_MS = 5 * 60 * 1000;
const FUNDO = '#b45309';
const BORDA = '#92400e';
const TEXTO = '#000';

function haQuanto(iso: string): string {
  const d = parseSPDate(iso);
  if (!d) return '';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 60) return `ha ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `ha ${h}h`;
  return `ha ${Math.floor(h / 24)}d`;
}

// Identificacao do contato pra exibir: nome + telefone formatado (cai pro que tiver).
function identContato(p: Perdido): string {
  const fone = p.telefone ? formatTelefoneBR(p.telefone) : '';
  if (p.nome) return fone ? `${p.nome} · ${fone}` : p.nome;
  return fone || p.jid;
}

// Pendencia de chamados PERDIDOS (cliente falou, caiu pra uma atendente e ninguem
// respondeu em 12h). So admin/gerente. "+" expande a lista com a atendente responsavel.
export function AlertaPerdidosBanner() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [itens, setItens] = useState<Perdido[]>([]);
  const [aberto, setAberto] = useState(false);

  const podeVer = usuario && ['admin', 'gerente'].includes(usuario.perfil);

  useEffect(() => {
    if (!podeVer) return;
    let cancelado = false;
    async function tick() {
      try {
        const r = await atendimentoApi.listarPerdidos();
        if (!cancelado) setItens(r.itens);
      } catch { /* silencia */ }
    }
    void tick();
    const id = setInterval(tick, POLLING_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [podeVer]);

  // Dispensar = reconhecer o perdido e tirar do aviso (NAO arquiva nem apaga a conversa).
  async function dispensar(id: number) {
    setItens((arr) => arr.filter((x) => x.id !== id)); // tira na hora (otimista)
    try {
      await atendimentoApi.dispensarPerdido(id);
    } catch {
      void atendimentoApi.listarPerdidos().then((r) => setItens(r.itens)).catch(() => {}); // recarrega se falhou
    }
  }

  const ativo = !!podeVer && itens.length > 0;
  const recolhido = useAlertaFaixa(
    'perdidos',
    ativo ? { rotulo: 'PERDIDO', bg: FUNDO, borda: BORDA, qtd: itens.length } : null,
  );

  if (!ativo || recolhido) return null;

  return (
    <div style={{ background: FUNDO, borderLeft: `4px solid ${BORDA}`, color: TEXTO, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px' }}>
        <button
          type="button"
          onClick={() => setAberto((o) => !o)}
          title={aberto ? 'Recolher' : 'Ver chamados perdidos'}
          style={{
            background: 'rgba(0,0,0,0.18)', color: TEXTO, border: 'none', borderRadius: 6, cursor: 'pointer',
            width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          {aberto ? <Minus size={15} /> : <Plus size={15} />}
        </button>
        <span style={{ fontWeight: 700, letterSpacing: 0.3, flexShrink: 0 }}>PERDIDO</span>
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {itens.length} sem retorno em 12h —{' '}
          <b>{itens.slice(0, 2).map(identContato).join('  •  ')}</b>
          {itens.length > 2 ? ` +${itens.length - 2}` : ''}
        </span>
      </div>

      {aberto && (
        <div style={{ padding: '0 14px 10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {itens.map((p) => (
            <div
              key={p.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(255,255,255,0.80)', border: `1px solid rgba(0,0,0,0.15)`,
                borderRadius: 6, padding: '6px 8px', color: '#000',
              }}
            >
              {/* <div role=button>, nao <button>: texto dentro de <button> nao se seleciona,
                  e o telefone precisa ser copiavel com o mouse. Com selecao ativa, nao navega. */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => { if (!window.getSelection()?.toString()) navigate('/atendimento'); }}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate('/atendimento'); }}
                title="Abrir no Atendimento"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                  background: 'none', border: 'none', cursor: 'pointer', color: '#000', padding: 0,
                  minWidth: 0, flex: 1,
                }}
              >
                <Avatar nome={p.nome || p.telefone || '?'} fotoUrl={p.foto_url} tamanho={26} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', userSelect: 'text', cursor: 'text' }}>
                    {identContato(p)}
                  </div>
                  <div style={{ fontSize: 11 }}>
                    Atendente: <b>{p.atendente_nome ? p.atendente_nome.replace(/\s+FIODECOBRE$/i, '') : '—'}</b> · {haQuanto(p.status_em)}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => dispensar(p.id)}
                title="Dispensar (tira do aviso, nao apaga a conversa)"
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
                  background: 'none', border: `1px solid rgba(0,0,0,0.25)`, borderRadius: 6,
                  cursor: 'pointer', color: '#000', padding: '3px 8px', fontSize: 11, fontWeight: 600,
                }}
              >
                <X size={13} /> Dispensar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
